import type { BigSource } from 'big.js';
import type { ReactNode } from 'react';
import type { DefaultTheme } from 'styled-components';
import type { IconName } from '../Icons';

export const NumberVariants = {
  Positive: 'positive',
  Negative: 'negative',
  Warning: 'warning',
  Primary: 'primary',
  Muted: 'muted',
} as const;
export type NumberVariants = (typeof NumberVariants)[keyof typeof NumberVariants];

export const SmartTruncateEnum = {
  None: 'None',
  Compact: 'Compact',
} as const;
export type SmartTruncateEnum = (typeof SmartTruncateEnum)[keyof typeof SmartTruncateEnum];

type InlineFormattedNumberBaseProps = {
  specification?: string;
  isSmall?: boolean;
  startIcon?: IconName;
  largeHighlight?: boolean;
  highlightAll?: boolean;
  currency?: string | null;
  /** Use currency for formatting/increment resolution without rendering the currency suffix. */
  showCurrency?: boolean;
  background?: string;
  align?: 'left' | 'right' | 'center';
  variant?: NumberVariants;
  pretty?: boolean;
  estimate?: boolean;
  theme?: DefaultTheme;
  highlightColor?: string;
  showSign?: boolean;
  prefix?: ReactNode;
  /** If the number is equal to an empty string, show a dash instead*/
  showDashIfEmpty?: boolean;
};

type NumericFormattingOptions = {
  /** Set the increment to be used for display. If left undefined, will default to currency.DefaultIncrement. If set to null, will leave increment unset. */
  increment?: string | null;
  truncate?: boolean;
  round?: boolean;
  /** When SmartTruncateEnum.Compact is used, decimals after 5dps will be truncated with "*"" */
  smartTruncate?: SmartTruncateEnum;
  smartTruncateThreshold?: BigSource;
  /** Whether or not to trim any trailing zeroes */
  trimTrailingZeroes?: boolean;
};

/**
 * Default numeric formatting (increment truncation, smart truncate, etc.).
 * @example <InlineFormattedNumber number="123.4567" increment="0.01" /> // 123.45*, hover: 123.4567
 */
export type InlineFormattedNumberFormattedProps = InlineFormattedNumberBaseProps &
  NumericFormattingOptions & {
    number?: BigSource;
    presentation?: never;
  };

/**
 * Preformatted text; when rounded, adds an asterisk and reveals full-precision `number` on hover.
 * @example <InlineFormattedNumber number="116.853515625" presentation={{ text: '116-273', isRounded: true }} />
 */
type InlineFormattedNumberPreformattedProps = InlineFormattedNumberBaseProps & {
  [Key in keyof NumericFormattingOptions]?: never;
} & {
  number: BigSource;
  presentation: { text: string; isRounded: boolean };
};

export type InlineFormattedNumberProps = InlineFormattedNumberFormattedProps | InlineFormattedNumberPreformattedProps;
